/* SM WORD V5 — Cart companions; progressive enhancement, delegated for Shopify section rerenders. */
(() => {
  if (window.__smWordCartV5Bound) return;
  window.__smWordCartV5Bound = true;
  // The price shown is always the selected variant's actual Shopify price.
  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-sm-cart-variant]');
    if (!select) return;
    const shown = select.closest('[data-sm-cart-card]')?.querySelector('[data-sm-cart-price]');
    const nextPrice = select.selectedOptions?.[0]?.dataset.smCartPrice;
    if (shown && nextPrice) shown.textContent = nextPrice;
  });
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-sm-cart-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const card = button.closest('[data-sm-cart-card]');
    const drawer = button.closest('cart-drawer');
    const error = card?.querySelector('[data-sm-cart-error]');
    const variant = card?.querySelector('[data-sm-cart-variant]');
    const id = variant?.value || button.dataset.smCartAdd;
    if (!id || !drawer?.getSectionsToRender || !drawer?.renderContents) {
      // Never leave a clickable Add button with no action.
      const productUrl = card?.querySelector('.sm-cart-rec__title')?.href;
      if (productUrl) window.location.assign(productUrl);
      else if (error) {
        error.hidden = false;
        error.textContent = (document.documentElement.lang || '').startsWith('fr')
          ? 'Ouvrez la fiche produit pour l’ajouter au panier.'
          : 'Open the product page to add this item to your bag.';
      }
      return;
    }
    const defaultLabel = button.querySelector('span')?.textContent || button.textContent;
    if (error) { error.hidden = true; error.textContent = ''; }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const label = button.querySelector('span');
    if (label) label.textContent = ({fr:'Ajout…',es:'Añadiendo…',it:'Aggiunta…',hr:'Dodavanje…'}[document.documentElement.lang?.slice(0,2)] || 'Adding…');
    try {
      const data = new FormData();
      data.append('id', id);
      data.append('quantity', '1');
      data.append('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
      data.append('sections_url', window.location.pathname);
      drawer.setActiveElement?.(document.activeElement);
      const response = await fetch(window.routes?.cart_add_url || '/cart/add', {
        method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: data, credentials: 'same-origin'
      });
      const payload = await response.json();
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Cart add failed');
      }
      // The add succeeded. A missing section response is a display error, not a failed order action.
      // Never invite the customer to retry the add: refresh the drawer, or open the cart.
      try {
        if (!payload.sections?.['cart-drawer']) {
          const cartUrl = window.routes?.cart_url || '/cart';
          const refreshResponse = await fetch(cartUrl + (cartUrl.includes('?') ? '&' : '?') +
            'sections=cart-drawer,cart-icon-bubble', {
            method: 'GET', credentials: 'same-origin', headers: { 'Accept': 'application/json' }
          });
          if (!refreshResponse.ok) throw new Error('Cart sections unavailable');
          const sections = await refreshResponse.json();
          payload.sections = { ...(payload.sections || {}), ...sections };
        }
        if (!payload.sections?.['cart-drawer']) throw new Error('Cart drawer unavailable');
        drawer.classList.remove('is-empty');
        drawer.renderContents(payload);
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          try {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'smword-cart-v5', productVariantId: id, cartData: payload
            });
          } catch (publishError) { console.warn('SM WORD cart event:', publishError); }
        }
      } catch (displayError) {
        // The line item was already added: safely continue to the canonical Shopify cart.
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
    } catch (err) {
      if (error) {
        error.textContent = ({
          fr:'Ajout impossible. Réessaie ou ouvre le produit.',
          es:'No se pudo añadir. Vuelve a intentarlo o abre el producto.',
          it:'Impossibile aggiungere. Riprova o apri il prodotto.',
          hr:'Dodavanje nije uspjelo. Pokušaj ponovno ili otvori proizvod.'
        }[document.documentElement.lang?.slice(0,2)] || 'Could not add. Try again or open the product.');
        error.hidden = false;
      }
      console.warn('SM WORD cart suggestion:', err);
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (label) label.textContent = defaultLabel;
      }
    }
  });

  // SM WORD CART V6.1 — reliable manual promo with native Shopify Ajax Cart API.
  // Never remove an existing discount just because a new code is invalid.
  let smPromoBusy = false;
  const smLocale = () => (document.documentElement.lang || 'en').slice(0, 2);
  const smMessages = {
    fr: {
      empty:'Saisissez un code promo.',
      busy:'Vérification du code…',
      already:'Ce code est déjà appliqué à votre panier.',
      applied:'Code accepté par Shopify. Le total a été actualisé.',
      unavailable:'Ce code ne s’applique pas à ce panier (conditions ou offres non cumulables).',
      restored:'Ce code n’est pas applicable. Votre réduction précédente est conservée.',
      uncertain:'Code envoyé à Shopify. Vérifiez le total au paiement.',
      failure:'Vérification impossible pour le moment. Réessayez.'
    },
    en: {
      empty:'Enter a promo code.',
      busy:'Checking your code…',
      already:'This code is already applied to your cart.',
      applied:'Code accepted by Shopify. Your total has been updated.',
      unavailable:'This code is not eligible for this cart or cannot be combined.',
      restored:'This code is not eligible. Your previous discount is preserved.',
      uncertain:'Code submitted to Shopify. Check the total at checkout.',
      failure:'Could not verify your code. Please try again.'
    },
    es: {
      empty:'Introduce un código.',
      busy:'Verificando el código…',
      already:'Este código ya está aplicado.',
      applied:'Código aceptado. Total actualizado.',
      unavailable:'El código no se aplica a esta cesta.',
      restored:'Código no válido. Se conserva el descuento anterior.',
      uncertain:'Código enviado a Shopify. Comprueba el total.',
      failure:'No se pudo verificar el código.'
    },
    it: {
      empty:'Inserisci un codice.',
      busy:'Verifica del codice…',
      already:'Questo codice è già applicato.',
      applied:'Codice accettato. Totale aggiornato.',
      unavailable:'Questo codice non si applica al carrello.',
      restored:'Codice non valido. Sconto precedente conservato.',
      uncertain:'Codice inviato a Shopify. Controlla il totale.',
      failure:'Impossibile verificare il codice.'
    },
    hr: {
      empty:'Unesite promotivni kod.',
      busy:'Provjera koda…',
      already:'Ovaj je kod već primijenjen.',
      applied:'Kod je prihvaćen. Ukupni iznos je ažuriran.',
      unavailable:'Kod nije primjenjiv na ovu košaricu.',
      restored:'Kod ne vrijedi. Prethodni popust je sačuvan.',
      uncertain:'Kod je poslan Shopifyju. Provjerite ukupni iznos.',
      failure:'Provjera koda nije uspjela.'
    }
  };
  const smText = kind => (smMessages[smLocale()] || smMessages.en)[kind];
  const smNormalize = code => String(code || '').trim().toLowerCase();
  const smShopifyRoot = () => {
    const fromShopify = window.Shopify?.routes?.root || '/';
    return fromShopify.endsWith('/') ? fromShopify : fromShopify + '/';
  };
  const smPromoSetStatus = (container, kind) => {
    const status = container?.querySelector('[data-sm-promo-status]');
    if (!status) return;
    status.hidden = false;
    status.textContent = smText(kind);
    status.classList.toggle('is-error', kind === 'failure' || kind === 'unavailable' || kind === 'restored');
  };
  const smCartRead = async () => {
    const response = await fetch(smShopifyRoot() + 'cart.js', {
      credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}
    });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  };
  const smDiscountApplications = cart => [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.items || []).flatMap(item =>
      (item.line_level_discount_allocations || []).map(alloc => alloc.discount_application || {}))
  ];
  const smCodeIsApplied = (cart, code) => {
    const c = smNormalize(code);
    if (!c) return false;
    const known = (cart.discount_codes || []).some(record =>
      smNormalize(record.code) === c && record.applicable !== false);
    const named = smDiscountApplications(cart).some(app =>
      smNormalize(app.title) === c && (Number(app.total_allocated_amount || 0) > 0 || app.type === 'discount_code'));
    return known || named;
  };
  const smKnownExistingCodes = cart => {
    const candidates = (cart.discount_codes || [])
      .filter(c => c.applicable !== false).map(c => String(c.code || '').trim());
    // Ajax carts can omit discount_codes. Recover exact code-like discount titles only.
    for (const app of smDiscountApplications(cart)) {
      if (app.type === 'automatic' || app.type === 'automatic_discount') continue;
      const label = String(app.title || '').trim();
      if (/^[A-Za-z0-9_-]{4,60}$/.test(label) && Number(app.total_allocated_amount || 0) > 0) {
        candidates.push(label);
      }
    }
    return [...new Set(candidates)].filter(Boolean);
  };
  const smDiscountSignature = cart => smDiscountApplications(cart)
    .map(app => [smNormalize(app.title),String(app.total_allocated_amount || 0),String(app.type || '')].join(':'))
    .sort().join('|');
  const smApplyNativeCode = async code => {
    const response = await fetch(smShopifyRoot() + 'cart/update.js', {
      method:'POST',
      credentials:'same-origin',
      headers:{'Content-Type':'application/json',Accept:'application/json'},
      body:JSON.stringify({discount:code})
    });
    const cart = await response.json();
    if (!response.ok || cart.status >= 400) {
      throw new Error(cart.description || cart.message || 'Discount update failed');
    }
    return cart;
  };
  const smPromoFlash = (code, state) => {
    try {
      sessionStorage.setItem('smword-promo-flash',JSON.stringify({code,state,expires:Date.now()+30000}));
    } catch (_) { /* optional progressive enhancement */ }
  };
  const smPromoRestoreFlash = () => {
    try {
      const stored = sessionStorage.getItem('smword-promo-flash');
      if (!stored) return;
      sessionStorage.removeItem('smword-promo-flash');
      const flash = JSON.parse(stored);
      if (!flash || Date.now() > flash.expires) return;
      const area = document.querySelector('[data-sm-cart-promo][data-sm-cart-context="cart"]');
      if (!area) return;
      area.open = true;
      const input = area.querySelector('[data-sm-promo-input]');
      if (input) input.value = flash.code || '';
      smPromoSetStatus(area, flash.state);
    } catch (_) { /* optional progressive enhancement */ }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',smPromoRestoreFlash,{once:true});
  else smPromoRestoreFlash();
  const smRefreshDrawer = async (code,state) => {
    const drawer = document.querySelector('cart-drawer');
    const response = await fetch(smShopifyRoot() + 'cart?sections=cart-drawer,cart-icon-bubble', {
      credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}
    });
    if (!response.ok || !drawer?.renderContents) throw new Error('Cart drawer refresh failed');
    const sections = await response.json();
    if (!sections['cart-drawer'] || !sections['cart-icon-bubble']) throw new Error('Cart sections unavailable');
    drawer.renderContents({id:null,sections});
    const area = drawer.querySelector('[data-sm-cart-promo][data-sm-cart-context="drawer"]');
    if (area) {
      area.open = true;
      const input = area.querySelector('[data-sm-promo-input]');
      if (input) input.value = code;
      smPromoSetStatus(area,state);
    }
  };
  document.addEventListener('keydown',event => {
    if (event.key !== 'Enter' || !event.target.matches?.('[data-sm-promo-input]')) return;
    event.preventDefault();
    event.target.closest('[data-sm-cart-promo]')?.querySelector('[data-sm-promo-apply]')?.click();
  });
  document.addEventListener('click',async event => {
    const button = event.target.closest?.('[data-sm-promo-apply]');
    if (!button || smPromoBusy) return;
    event.preventDefault();
    const area = button.closest('[data-sm-cart-promo]');
    const input = area?.querySelector('[data-sm-promo-input]');
    const code = String(input?.value || '').trim();
    if (!code) {
      smPromoSetStatus(area,'empty');
      input?.focus();
      return;
    }
    smPromoBusy = true;
    button.disabled = true;
    button.setAttribute('aria-busy','true');
    smPromoSetStatus(area,'busy');
    try {
      const before = await smCartRead();
      if (smCodeIsApplied(before,code)) {
        smPromoSetStatus(area,'already');
        return;
      }
      const previousCodes = smKnownExistingCodes(before);
      const prevSig = smDiscountSignature(before);
      await smApplyNativeCode(code);
      let after = await smCartRead();
      const explicit = smCodeIsApplied(after,code);
      const changed = smDiscountSignature(after) !== prevSig;
      const possibleDiscountImprovement = Number(after.total_discount || 0) > Number(before.total_discount || 0);
      const accepted = explicit || (changed && smDiscountApplications(after).length > 0) || possibleDiscountImprovement;
      let state = accepted ? 'applied' : 'unavailable';
      if (!accepted && previousCodes.length) {
        // Shopify's discount parameter replaces previous codes. Restore them if the new code is not eligible.
        await smApplyNativeCode(previousCodes.join(','));
        after = await smCartRead();
        state = previousCodes.some(prev => smCodeIsApplied(after,prev)) ? 'restored' : 'uncertain';
      }
      if (area.dataset.smCartContext === 'drawer') {
        await smRefreshDrawer(code,state);
      } else {
        smPromoFlash(code,state);
        window.location.reload(); // refresh Shopify's actual cart totals & lines, never invent client prices
      }
    } catch (error) {
      smPromoSetStatus(area,'failure');
      console.warn('SM WORD promo:',error);
    } finally {
      smPromoBusy = false;
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  });

})();